import { RecaptchaVerifier } from 'firebase/auth';

// Global variable to track current reCAPTCHA process
let currentRecaptchaProcess = null;

/**
 * Clean up all reCAPTCHA elements from DOM
 */
export const cleanupAllRecaptchaElements = () => {
  const selectors = [
    '[data-sitekey]', 
    '.grecaptcha-badge', 
    'iframe[src*="recaptcha"]',
    '[id*="recaptcha-"][id*="-"]'
  ];
  
  selectors.forEach(selector => {
    const elements = document.querySelectorAll(selector);
    if (!elements) return;
    elements.forEach(element => {
      try {
        if (selector.includes('recaptcha-') && selector.includes('-')) {
          const timestampMatch = element.id.match(/recaptcha-(\d+)-/);
          if (!timestampMatch) return;
        }
        
        if (element.parentNode) {
          element.parentNode.removeChild(element);
        }
      } catch (error) {
        console.error('Element removal error:', error);
      }
    });
  });
};

/**
 * Clean up existing reCAPTCHA containers except specified one
 * @param {string} originalContainerId - ID of container to preserve
 */
export const cleanupOtherRecaptchaContainers = (originalContainerId) => {
  const allContainers = document.querySelectorAll('[id*="recaptcha"]');
  if (!allContainers) return;
  allContainers.forEach(container => {
    if (container.id !== originalContainerId) {
      container.remove();
    }
  });
  
  const originalContainer = document.getElementById(originalContainerId);
  if (originalContainer) {
    originalContainer.innerHTML = '';
  }
};

/**
 * Clean up reCAPTCHA verifier
 */
export const cleanupRecaptchaVerifier = () => {
  try {
    if (window.recaptchaVerifier) {
      try {
        window.recaptchaVerifier.clear();
      } catch (clearError) {
        console.error('reCAPTCHA clear error:', clearError);
      }
      window.recaptchaVerifier = null;
      delete window.recaptchaVerifier;
    }

    cleanupAllRecaptchaElements();
  } catch (error) {
    console.error('reCAPTCHA cleanup error:', error);
  }
};

/**
 * Prepare clean reCAPTCHA environment
 * @param {string} originalContainerId - Original container ID
 * @param {number} cleanupDelay - Delay before cleanup (default: 500ms)
 */
export const prepareCleanRecaptchaEnvironment = async (originalContainerId, cleanupDelay = 500) => {
  if (currentRecaptchaProcess) {
    currentRecaptchaProcess.cancelled = true;
    console.info('Cancelling previous reCAPTCHA process');
  }

  cleanupRecaptchaVerifier();
  cleanupOtherRecaptchaContainers(originalContainerId);
  
  await new Promise(resolve => setTimeout(resolve, cleanupDelay));
};

/**
 * Create reCAPTCHA verifier
 * @param {string} containerId - Container ID
 * @param {Object} auth - Firebase authentication instance
 * @param {Object} callbacks - Callbacks for reCAPTCHA events
 * @param {Object} options - Additional reCAPTCHA options
 * @returns {RecaptchaVerifier} Verifier instance
 */
export const createRecaptchaVerifier = (containerId, auth, callbacks, options = {}) => {
  
  const defaultOptions = {
    size: 'invisible',
    callback: callbacks.onResolved,
    'expired-callback': callbacks.onExpired,
    'error-callback': callbacks.onError,
    'cancel-callback': callbacks.onCancelled,
  };
  
  return new RecaptchaVerifier(
    containerId,
    { ...defaultOptions, ...options },
    auth
  );
};

/**
 * Check reCAPTCHA state
 * @param {Object} recaptchaState - Current reCAPTCHA state
 * @param {Object} stateActions - State action constants
 * @returns {string|null} Action to perform or null
 */
export const checkRecaptchaState = (recaptchaState, stateActions) => {
  if (currentRecaptchaProcess !== recaptchaState) {
    return stateActions.REPLACED;
  }
  
  if (recaptchaState.resolved) {
    return stateActions.RESOLVED;
  }
  
  if (recaptchaState.cancelled) {
    return stateActions.CANCELLED;
  }

  const recaptchaFrame = document.querySelector('iframe[src*="recaptcha"]');
  const recaptchaDiv = document.querySelector('[data-sitekey]');
  
  if (!recaptchaFrame && !recaptchaDiv && window.recaptchaVerifier && !recaptchaState.resolved) {
    return stateActions.CLOSED_EXTERNALLY;
  }

  return null;
};

/**
 * Create monitor to watch reCAPTCHA state
 * @param {Object} recaptchaState - reCAPTCHA state to monitor
 * @param {Object} config - Configuration object
 * @param {Object} config.stateActions - State action constants
 * @param {number} config.statusCheckInterval - Check interval in ms (default: 500)
 * @param {number} config.timeout - Timeout in ms (default: 120000)
 * @returns {Promise} Promise that resolves or rejects based on state
 */
export const createRecaptchaStatusMonitor = (recaptchaState, config = {}) => {
  const {
    stateActions,
    statusCheckInterval = 500,
    timeout = 120000
  } = config;

  return new Promise((resolve, reject) => {
    const interval = setInterval(() => {
      const action = checkRecaptchaState(recaptchaState, stateActions);
      
      switch (action) {
        case stateActions.REPLACED:
          clearInterval(interval);
          console.info('reCAPTCHA process replaced by new one');
          return;
          
        case stateActions.RESOLVED:
          clearInterval(interval);
          resolve('resolved');
          return;
          
        case stateActions.CANCELLED:
          clearInterval(interval);
          reject(new Error('User cancelled'));
          return;
          
        case stateActions.CLOSED_EXTERNALLY:
          clearInterval(interval);
          console.info('reCAPTCHA closed by external click');
          recaptchaState.cancelled = true;
          cleanupRecaptchaVerifier();
          reject(new Error('User cancelled'));
          return;
      }
    }, statusCheckInterval);

    setTimeout(() => {
      clearInterval(interval);
      if (currentRecaptchaProcess === recaptchaState && !recaptchaState.resolved && !recaptchaState.cancelled) {
        console.info('reCAPTCHA timeout - deadline exceeded');
        recaptchaState.cancelled = true;
        cleanupRecaptchaVerifier();
        reject(new Error('reCAPTCHA timeout'));
      }
    }, timeout);
  });
};

/**
 * Create callbacks for reCAPTCHA
 * @param {Object} recaptchaState - reCAPTCHA state
 * @param {Function} errorHandler - Optional custom error handler
 * @returns {Object} Configured callbacks
 */
export const createRecaptchaCallbacks = (recaptchaState, errorHandler = null) => ({
  onResolved: () => {
    console.info('reCAPTCHA resolved');
    recaptchaState.resolved = true;
  },
  onExpired: () => {
    console.info('reCAPTCHA expired');
    recaptchaState.cancelled = true;
    cleanupRecaptchaVerifier();
  },
  onError: (error) => {
    if (errorHandler) {
      errorHandler(error);
    } else {
      console.error('reCAPTCHA error:', error);
    }
    recaptchaState.cancelled = true;
    cleanupRecaptchaVerifier();
  },
  onCancelled: () => {
    console.info('reCAPTCHA cancelled by user');
    recaptchaState.cancelled = true;
    cleanupRecaptchaVerifier();
  }
});

/**
 * Getters and setters for current reCAPTCHA process
 */
export const getCurrentRecaptchaProcess = () => currentRecaptchaProcess;
export const setCurrentRecaptchaProcess = (process) => {
  currentRecaptchaProcess = process;
};
export const clearCurrentRecaptchaProcess = () => {
  currentRecaptchaProcess = null;
};

/**
 * Generate unique ID for reCAPTCHA containers
 * @returns {string} Unique ID
 */
export const generateUniqueRecaptchaId = () => 
  `recaptcha-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;

/**
 * Create hidden container in DOM
 * @param {string} id - Container ID
 * @returns {HTMLElement} Created element
 */
export const createHiddenContainer = (id) => {
  const container = document.createElement('div');
  container.id = id;
  container.style.display = 'none';
  document.body.appendChild(container);
  return container;
};

/**
 * Remove element from DOM by ID
 * @param {string} id - ID of element to remove
 */
export const removeElementById = (id) => {
  const element = document.getElementById(id);
  if (element) {
    element.remove();
    console.info('Container removed:', id);
  }
}; 